import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./schema";
import { toApiError } from "./errors";
import { ensureIdempotencyKey } from "./idempotency";
import { API_BASE_URL } from "@/lib/config";

const baseUrl = API_BASE_URL;

// --- Pluggable hooks wired up by the auth layer (avoids an import cycle) ---

let accessTokenGetter: () => string | null = () => null;
/** Register how the client obtains the current bearer token. */
export function registerAccessTokenGetter(fn: () => string | null): void {
	accessTokenGetter = fn;
}

let unauthorizedHandler: (response: Response) => void = () => {};
/** Register a callback invoked when the API returns 401 (drives refresh/redirect). */
export function registerUnauthorizedHandler(
	fn: (response: Response) => void,
): void {
	unauthorizedHandler = fn;
}

const authMiddleware: Middleware = {
	onRequest({ request, schemaPath }) {
		ensureIdempotencyKey(request, schemaPath);
		const token = accessTokenGetter();
		if (token && !request.headers.has("Authorization")) {
			request.headers.set("Authorization", `Bearer ${token}`);
		}
		return request;
	},
	onResponse({ response }) {
		if (response.status === 401) {
			unauthorizedHandler(response);
		}
		return response;
	},
};

/** Typed openapi-fetch client for the nextsub API. */
export const api = createClient<paths>({ baseUrl });
api.use(authMiddleware);

/**
 * Unwrap an openapi-fetch result into its data, throwing a normalised ApiError
 * on failure. Use in query/mutation functions so TanStack Query receives a
 * consistent error type.
 */
export function unwrap<T>(result: {
	data?: T;
	error?: unknown;
	response: Response;
}): T {
	if (result.error !== undefined || !result.response.ok) {
		throw toApiError(result.error, result.response);
	}
	return result.data as T;
}
